import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import Spinner from "./Spinner";
import {
  IconAlertCircle,
  IconCheck,
  IconInfo,
  IconX,
} from "./icons";

export type ToastKind = "success" | "error" | "info" | "loading";

export interface ToastItem {
  id: string;
  kind: ToastKind;
  message: string;
  description?: string;
}

interface ToastApi {
  show: (t: Omit<ToastItem, "id"> & { id?: string }) => string;
  success: (message: string, description?: string) => string;
  error: (message: string, description?: string) => string;
  info: (message: string, description?: string) => string;
  loading: (message: string, description?: string) => string;
  update: (id: string, patch: Partial<Omit<ToastItem, "id">>) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const AUTO_DISMISS_MS: Record<ToastKind, number | null> = {
  success: 4000,
  error: 6000,
  info: 4000,
  loading: null,
};

let counter = 0;
const nextId = () => `t${Date.now().toString(36)}${(counter++).toString(36)}`;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const clearTimer = useCallback((id: string) => {
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
  }, []);

  const dismiss = useCallback(
    (id: string) => {
      clearTimer(id);
      setItems((prev) => prev.filter((t) => t.id !== id));
    },
    [clearTimer]
  );

  const scheduleDismiss = useCallback(
    (id: string, kind: ToastKind) => {
      clearTimer(id);
      const ms = AUTO_DISMISS_MS[kind];
      if (ms !== null) {
        const handle = setTimeout(() => dismiss(id), ms);
        timers.current.set(id, handle);
      }
    },
    [clearTimer, dismiss]
  );

  const show = useCallback<ToastApi["show"]>(
    (t) => {
      const id = t.id ?? nextId();
      setItems((prev) => {
        const existing = prev.findIndex((p) => p.id === id);
        const next: ToastItem = {
          id,
          kind: t.kind,
          message: t.message,
          description: t.description,
        };
        if (existing >= 0) {
          const copy = [...prev];
          copy[existing] = next;
          return copy.slice(-5);
        }
        return [...prev, next].slice(-5);
      });
      scheduleDismiss(id, t.kind);
      return id;
    },
    [scheduleDismiss]
  );

  const update = useCallback<ToastApi["update"]>(
    (id, patch) => {
      setItems((prev) => {
        const i = prev.findIndex((p) => p.id === id);
        if (i < 0) return prev;
        const copy = [...prev];
        copy[i] = { ...copy[i], ...patch };
        return copy;
      });
      if (patch.kind) scheduleDismiss(id, patch.kind);
    },
    [scheduleDismiss]
  );

  useEffect(() => {
    const t = timers.current;
    return () => {
      t.forEach((h) => clearTimeout(h));
      t.clear();
    };
  }, []);

  const api: ToastApi = useMemo(
    () => ({
      show,
      success: (m, d) => show({ kind: "success", message: m, description: d }),
      error: (m, d) => show({ kind: "error", message: m, description: d }),
      info: (m, d) => show({ kind: "info", message: m, description: d }),
      loading: (m, d) => show({ kind: "loading", message: m, description: d }),
      update,
      dismiss,
    }),
    [show, update, dismiss]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport items={items} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

function ToastViewport({
  items,
  onDismiss,
}: {
  items: ToastItem[];
  onDismiss: (id: string) => void;
}) {
  return (
    <div
      className="pointer-events-none fixed z-50 inset-0 flex flex-col items-end justify-end gap-2 p-4 sm:p-6"
      aria-live="polite"
    >
      {items.map((t) => (
        <ToastRow key={t.id} item={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastRow({
  item,
  onDismiss,
}: {
  item: ToastItem;
  onDismiss: (id: string) => void;
}) {
  const palette: Record<ToastKind, { bg: string; border: string; icon: ReactNode }> = {
    success: {
      bg: "bg-white",
      border: "border-mint-200",
      icon: <IconCheck size={18} className="text-mint-600" />,
    },
    error: {
      bg: "bg-white",
      border: "border-red-200",
      icon: <IconAlertCircle size={18} className="text-red-600" />,
    },
    info: {
      bg: "bg-white",
      border: "border-brand-200",
      icon: <IconInfo size={18} className="text-brand-600" />,
    },
    loading: {
      bg: "bg-white",
      border: "border-brand-200",
      icon: <Spinner size={18} className="text-brand-600" />,
    },
  };
  const p = palette[item.kind];
  return (
    <div
      className={`pointer-events-auto toast-enter min-w-[260px] max-w-sm shadow-lg shadow-ink-900/10 rounded-lg border ${p.border} ${p.bg} pl-3 pr-2 py-2.5 flex items-start gap-2.5`}
      role={item.kind === "error" ? "alert" : "status"}
    >
      <div className="mt-0.5 shrink-0">{p.icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-ink-800">{item.message}</p>
        {item.description && (
          <p className="text-xs text-ink-500 mt-0.5">{item.description}</p>
        )}
      </div>
      <button
        onClick={() => onDismiss(item.id)}
        className="shrink-0 text-ink-400 hover:text-ink-700 p-1 rounded-md"
        aria-label="Dismiss"
      >
        <IconX size={14} />
      </button>
    </div>
  );
}
